<template>
  <div ref="containerRef" class="diagnostic-chart-render-target"></div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import type { DiagnosticSeries } from '@/services/diagnosticChartService'

const props = defineProps<{
  series: DiagnosticSeries[]
}>()

const containerRef = ref<HTMLElement | null>(null)
let root: am5.Root | null = null

const createChart = () => {
  if (!containerRef.value) return
  root?.dispose()

  root = am5.Root.new(containerRef.value)
  root._logo?.dispose()
  root.setThemes([am5themes_Animated.new(root)])

  const chart = root.container.children.push(am5xy.XYChart.new(root, {
    panX: true,
    panY: true,
    wheelX: 'panX',
    wheelY: 'zoomX',
    maxTooltipDistance: 0,
    pinchZoomX: true,
    paddingRight: 8,
  }))

  // Pin the axis just past the last development period. Left to itself amCharts
  // rounds the max up to the next "nice" step, which on this narrow plot means
  // a step of 50 and a wide empty tail.
  const lastDev = props.series.reduce(
    (max, input) => Math.max(max, input.points.length ? input.points[input.points.length - 1].dev : 0),
    0
  )
  const axisMax = lastDev ? Math.ceil((lastDev + 1) / 10) * 10 : undefined

  const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
    renderer: am5xy.AxisRendererX.new(root, {}),
    tooltip: am5.Tooltip.new(root, {}),
    min: 0,
    max: axisMax,
    strictMinMax: true,
    maxDeviation: 0,
  }))
  const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
    renderer: am5xy.AxisRendererY.new(root, {}),
    numberFormat: "#'%'",
  }))

  xAxis.children.push(am5.Label.new(root, {
    text: 'Development',
    x: am5.p50,
    centerX: am5.p50,
  }))
  yAxis.children.unshift(am5.Label.new(root, {
    text: 'Percent(%)',
    rotation: -90,
    y: am5.p50,
    centerX: am5.p50,
  }))

  let selectedSeries: am5xy.LineSeries | null = null

  for (const input of props.series) {
    // Omit stroke/fill entirely for unselected series: amCharts treats a key
    // present with an undefined value as user-set and skips the theme colour,
    // which draws the line with no stroke at all.
    const settings: am5xy.ILineSeriesSettings = {
      name: input.name,
      xAxis,
      yAxis,
      valueXField: 'dev',
      valueYField: 'value',
      legendValueText: '{valueY}%',
      connect: false,
      tooltip: am5.Tooltip.new(root, { labelText: '{name}: {valueY}%' }),
    }
    if (input.selected) {
      settings.stroke = am5.color(0xff0000)
      settings.fill = am5.color(0xff0000)
    }

    const chartSeries = chart.series.push(am5xy.LineSeries.new(root, settings))
    chartSeries.data.setAll(input.points)
    if (input.selected) {
      chartSeries.strokes.template.setAll({ strokeWidth: 4 })
      selectedSeries = chartSeries
    }
    chartSeries.appear()
  }

  selectedSeries?.toFront()

  const cursor = chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none' }))
  cursor.lineY.set('visible', false)

  const legend = chart.rightAxesContainer.children.push(am5.Legend.new(root, {
    width: 150,
    height: am5.percent(100),
    paddingLeft: 8,
    verticalScrollbar: am5.Scrollbar.new(root, { orientation: 'vertical' }),
  }))
  legend.itemContainers.template.setAll({ paddingTop: 1, paddingBottom: 1 })
  legend.markers.template.setAll({ width: 10, height: 10 })
  legend.labels.template.setAll({
    fontSize: 10,
    maxWidth: 88,
    oversizedBehavior: 'truncate',
  })
  legend.valueLabels.template.setAll({ fontSize: 10 })
  legend.data.setAll(chart.series.values)
  chart.appear(500, 50)
}

onMounted(createChart)
watch(() => props.series, createChart, { deep: true })
onBeforeUnmount(() => root?.dispose())
</script>

<style scoped>
.diagnostic-chart-render-target {
  width: 100%;
  height: 100%;
  background: white;
}
</style>
